import { Button } from "@progress/kendo-react-buttons";
import { DateTimePicker } from "@progress/kendo-react-dateinputs";
import { Window } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";
import { Slider, SliderLabel } from "@progress/kendo-react-inputs";
import { Error, Label } from "@progress/kendo-react-labels";
import axios from "axios";
import { useEffect, useState } from "react";
import { allTemplates, userEmail, userEntries } from "../../Common/Store/CommonSignals";
import { Else, If } from "../../Common/UI/IfComponent";
import { AdaptiveField } from "../../Fields/FieldTypes/FieldFactory";
import ConfirmSplitEntryDialog from "../../Notification/ConfirmSplitEntryDialog";
import { handleSignalRTrigger } from "../../Utilities/CommonUtils";
import {
  entryId,
  entryValues,
  formErrors,
  isSaving,
  template,
  templateId,
} from "../Manage/EditEntrySignals";
import { showSplitEntryDialog, showSplitEntryWindow } from "../View/ViewEntrySignals";

export const SplitEntryWindow = () => {
  const [sliderValue, setSliderValue] = useState(0);
  const [originalRecord, setOriginalRecord] = useState(null);
  const [splittedRecord, setSplittedRecord] = useState(null);
  const [event, setEvent] = useState(null);
  const [hasEndTime, setHasEndTime] = useState(true);
  const buttonValidationMessage = "There are missing required fields";

  const renderFields = [
    "Equipment",
    "Primary Element",
    "Secondary Element",
    "Tertiary Element",
    "WO Number",
    "Comments",
    "Action Raised",
  ];

  // Initialize originalRecord and splittedRecord
  useEffect(() => {
    setOriginalRecord(null);
    setSplittedRecord(null);
    setSliderValue(0);

    if (
      !showSplitEntryWindow.value ||
      !entryId.value ||
      !templateId.value ||
      !userEntries?.value?.[templateId.value]
    ) {
      return;
    }

    const record = JSON.parse(
      JSON.stringify(userEntries.value[templateId.value].find((item) => item.id === entryId.value))
    );
    const delay_event = record.sections[0].fields.find(
      (field) => field.fieldName === "Delay_Event"
    )?.value;
    entryValues.value = {
      ...entryValues.value,
      Delay_Event_original: delay_event,
      Delay_Event_splitted: delay_event,
    };
    const subArea = record.sections[0].fields.find(
      (field) => field.fieldName === "Sub-Area"
    )?.value;
    entryValues.value = {
      ...entryValues.value,
      "Sub-Area_original": subArea,
      "Sub-Area_splitted": subArea,
    };
    setOriginalRecord(record ?? null);

    const endTimeField = record?.sections[0]?.fields.find(
      (field) => field.fieldName === "Event Frame End Time"
    );

    if (!endTimeField?.value) {
      setHasEndTime(false);
    } else {
      setHasEndTime(true);
    }

    if (record) {
      let splitRecord = JSON.parse(JSON.stringify(record));
      splitRecord.sections?.forEach((section) => {
        section.fields?.forEach((field) => {
          if (
            [
              "Primary Element",
              "Secondary Element",
              "Tertiary Element",
              "WO Number",
              "Comments",
            ].includes(field.fieldName)
          ) {
            field.value = "";
          }
        });
      });
      setSplittedRecord(splitRecord);
    }

    if (!template.value) {
      template.value = allTemplates.value.find((x) => x.id === templateId.value);
    }
  }, [entryId.value, templateId.value, showSplitEntryWindow.value]);

  if (!originalRecord) return null;
  if (!showSplitEntryWindow.value) return null;

  // Slider control values
  const startTime = new Date(
    originalRecord?.sections[0]?.fields.find(
      (field) => field.fieldName === "Event Frame Start Time"
    )?.value
  );
  const endTime = hasEndTime
    ? new Date(
        originalRecord.sections[0].fields.find(
          (field) => field.fieldName === "Event Frame End Time"
        ).value
      )
    : new Date();

  const totalMinutes = Math.floor((endTime - startTime) / (1000 * 60));
  const splitPoint = new Date(startTime.getTime() + sliderValue * 60 * 1000);
  const sliderLabels = [];
  for (let i = 0; i <= totalMinutes; i += totalMinutes / 10) {
    sliderLabels.push(parseFloat(i.toFixed(2)));
  }

  // Update records time values
  const updatedOriginalStartTime = hasEndTime ? startTime : splitPoint;
  const updatedOriginalEndTime = hasEndTime ? splitPoint : null;
  const updatedSplittedStartTime = hasEndTime ? splitPoint : startTime;
  const updatedSplittedEndTime = hasEndTime ? endTime : splitPoint;

  const originalRecordDuration = hasEndTime
    ? Math.floor((updatedOriginalEndTime - updatedOriginalStartTime) / (1000 * 60))
    : null;
  const splittedRecordDuration = Math.floor(
    (updatedSplittedEndTime - updatedSplittedStartTime) / (1000 * 60)
  );

  const formatDate = (date) => {
    if (!date) {
      return null;
    }

    const months = date.getMonth() + 1;
    const days = date.getDate();
    const years = date.getFullYear().toString().slice(-2);
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${months}/${days}/${years}, ${hours}:${minutes} ${ampm}`;
  };

  // Updates start time, end time and duration of the record with the selected values in the slider
  const updateTimeFields = (record, newStartTime, newEndTime) => {
    const updatedRecord = JSON.parse(JSON.stringify(record));

    updatedRecord.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.fieldName === "Event Frame Start Time") {
          const timeZoneOffset = newStartTime.getTimezoneOffset() * 60000;
          const adjustedDate = new Date(newStartTime.getTime() - timeZoneOffset)
            .toISOString()
            .replace("Z", "");
          field.value = adjustedDate;
        }
        if (field.fieldName === "Event Frame End Time" && newEndTime) {
          const timeZoneOffset = newEndTime.getTimezoneOffset() * 60000;
          const adjustedDate = new Date(newEndTime.getTime() - timeZoneOffset)
            .toISOString()
            .replace("Z", "");
          field.value = adjustedDate;
        }

        if (field.fieldName === "Event Frame Duration (Min)" && newEndTime) {
          field.value = Math.floor((newEndTime - newStartTime) / (1000 * 60));
        }

        if (field.fieldType === "ContinuousComments") {
          let comments = JSON.tryParse(field.value, []);

          comments.forEach((c) => {
            if (c.isTemporary) {
              delete c.isTemporary;
            }
          });

          field.value = JSON.stringify(comments);
        }
      });
    });

    return updatedRecord;
  };

  // Formats the split record by removing the original ID and assigning a new Id with a suffix
  const formatSplittedRecord = (record) => {
    const updatedSplittedRecord = { ...record, id: "" };

    const entries = userEntries?.value?.[templateId.value] || [];
    const existingIds = entries.flatMap((entry) =>
      entry.sections.flatMap((section) =>
        section.fields.filter((field) => field.fieldName === "Id").map((field) => field.value)
      )
    );

    const currentId = record.sections
      .flatMap((section) => section.fields)
      .find((field) => field.fieldName === "Id")?.value;

    const originalId = currentId.replace(/\.\d+$/, "");

    const suffixedIds = existingIds.filter((eventId) => eventId.startsWith(`${originalId}.`));

    const newSuffix = suffixedIds.length + 1;

    updatedSplittedRecord.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.fieldName === "Id") {
          field.value = `${originalId}.${newSuffix}`;
        }
      });
    });

    return updatedSplittedRecord;
  };

  const handleSliderChange = (e) => {
    const integerValue = Math.floor(e.value);
    setSliderValue(integerValue);
  };

  const handleFieldChange = ({ e }) => {
    entryValues.value = { ...entryValues.value, [e.target.name]: e.value };

    if (e.target.name.endsWith("_original")) {
      originalRecord.sections.forEach((section) => {
        section.fields.forEach((field) => {
          if (field.fieldName === e.target.name.split("_")[0]) {
            field.value = e.value;
          }
        });
      });
    }
    if (e.target.name.endsWith("_splitted")) {
      splittedRecord.sections.forEach((section) => {
        section.fields.forEach((field) => {
          if (field.fieldName === e.target.name.split("_")[0]) {
            field.value = e.value;
          }
        });
      });
    }
  };

  const handleDateTimeChange = (e) => {
    const selectedDate = e.value;
    const diffInMinutes = Math.floor((selectedDate - startTime) / (1000 * 60));
    const clampedValue = Math.max(0, Math.min(totalMinutes, diffInMinutes));

    setSliderValue(clampedValue);
  };

  const handleCommitClick = (e) => {
    setEvent(e);
    showSplitEntryDialog.value = true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showSplitEntryDialog.value = false;
    isSaving.value = true;

    try {
      let updatedOriginalRecord = updateTimeFields(
        originalRecord,
        updatedOriginalStartTime,
        updatedOriginalEndTime
      );
      let updateSplittedRecord = updateTimeFields(
        splittedRecord,
        updatedSplittedStartTime,
        updatedSplittedEndTime
      );

      let createSplittedRecord = formatSplittedRecord(updateSplittedRecord);
      // Update original record
      let response;
      response = await axios.put(
        `${process.env.REACT_APP_API_ENDPOINT}/api/Entry/${entryId.value}?userId=${
          userEmail.value
        }&templateSync=${template.value?.properties?.["TemplateSync"] ?? false}`,
        updatedOriginalRecord
      );
      await handleSignalRTrigger("UPDATE", "Entry", updatedOriginalRecord);

      // Add splitted record
      response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/api/Entry?userId=${userEmail}&templateSync=${
          template.value?.properties["TemplateSync"] ?? false
        }`,
        createSplittedRecord
      );
      createSplittedRecord.id = response.data.id;

      await handleSignalRTrigger("CREATE", "Entry", response.data);

      const newEntries = { ...userEntries.value };
      const entryIndex = newEntries[templateId.value].findIndex(
        (entry) => entry.id === updatedOriginalRecord.id
      );
      newEntries[templateId.value][entryIndex] = updatedOriginalRecord;
      newEntries[templateId.value].splice(entryIndex, 0, createSplittedRecord);
      userEntries.value = newEntries;

      showSplitEntryWindow.value = false;
      setSliderValue(0);

      isSaving.value = false;
    } catch (error) {
      console.log("Failed to submit split record: ", error);
    }
  };

  return (
    <Window
      title="Split Record"
      onClose={() => {
        showSplitEntryWindow.value = false;
        entryValues.value = {};
      }}
      initialHeight={950}
      initialWidth={1100}
    >
      <If condition={!isSaving.value}>
        <div
          style={{
            position: "sticky",
            top: "-12px",
            backgroundColor: "var(--atlas-bg-primary)",
            zIndex: 100,
            height: "3.5rem",
          }}
        >
          {/* Commit Button */}
          <Button
            themeColor="primary"
            disabled={
              originalRecordDuration === 0 ||
              splittedRecordDuration === 0 ||
              Object.values(formErrors.value).some((sectionErrors) => sectionErrors.length > 0)
            }
            onClick={(e) => {
              handleCommitClick(e);
            }}
          >
            Commit Split
          </Button>
          {Object.values(formErrors.value).some((sectionErrors) => sectionErrors.length > 0) >
            0 && <Error>{buttonValidationMessage}</Error>}
        </div>

        {/* Slider */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Label
            className="mb-3"
            style={{ fontSize: "18px", fontWeight: "bold" }}
          >
            Adjust Downtime
          </Label>
          <br />
          <Slider
            buttons={true}
            min={0}
            max={totalMinutes}
            step={1}
            value={sliderValue}
            onChange={handleSliderChange}
            style={{ width: "85%" }}
          >
            {sliderLabels.map((position) => (
              <SliderLabel
                key={position}
                position={position}
              >
                {position}
              </SliderLabel>
            ))}
          </Slider>
          <div style={{ marginTop: "20px", fontSize: "16px", fontWeight: "bold" }}>
            <Label>Selected Time: </Label>
            <DateTimePicker
              value={splitPoint}
              onChange={handleDateTimeChange}
              min={startTime}
              max={endTime}
              format="MM/dd/yy, h:mm a"
              width={175}
              fillMode={"outline"}
            />
          </div>

          <div style={{ fontSize: "14px", color: "#666" }}>
            {Math.floor(sliderValue)} minutes from start time
          </div>
        </div>

        {/* Data */}

        <div
          className="k-form"
          style={{ display: "flex" }}
        >
          <div style={{ fontSize: 18, display: "inline-block", marginLeft: "5%", marginTop: "3%" }}>
            <Label style={{ fontWeight: "bold" }}>First Event</Label>
            <div>
              <strong>Start Time:</strong> {formatDate(updatedOriginalStartTime)}
            </div>
            <div>
              <strong>End Time:</strong> {formatDate(updatedOriginalEndTime)}
            </div>
            <div>
              <strong>Event Frame Duration (Min):</strong> {originalRecordDuration}
            </div>

            <div style={{ width: "120%" }}>
              {originalRecord.sections.flatMap((section, sectionIndex) =>
                section.fields
                  .filter((field) => renderFields.includes(field.fieldName))
                  .map((field, fieldIndex) => {
                    let newParentFieldName = "";
                    if (field.properties.ParentFieldName) {
                      newParentFieldName = field.properties.ParentFieldName + "_original";
                    }
                    field = {
                      ...field,
                      fieldName: field.fieldName + "_original",
                      properties: {
                        ...field.properties,
                        ParentFieldName: newParentFieldName,
                        Label: field.fieldName,
                      },
                    };

                    const props = {
                      field: field,
                      fieldName: field?.fieldName,
                      fieldType: field?.fieldType,
                      sectionIndex: sectionIndex,
                      fieldIndex: fieldIndex,
                      value: field?.value || "",
                      handleFieldChange: handleFieldChange,
                      labelVisible: true,
                      isTabField: false,
                      isStageField: false,
                      isPrintPreview: false,
                    };

                    return (
                      <div
                        key={field.fieldName}
                        className="k-mt-2"
                      >
                        <AdaptiveField {...props} />
                      </div>
                    );
                  })
              )}
            </div>
          </div>

          <div
            style={{ fontSize: 18, display: "inline-block", marginLeft: "10%", marginTop: "3%" }}
          >
            <Label style={{ fontWeight: "bold" }}>Second Event</Label>
            <div>
              <strong>Start Time:</strong> {formatDate(updatedSplittedStartTime)}
            </div>
            <div>
              <strong>End Time:</strong> {formatDate(updatedSplittedEndTime)}
            </div>
            <div>
              <strong>Event Frame Duration (Min):</strong> {splittedRecordDuration}
            </div>

            <div style={{ width: "120%" }}>
              {splittedRecord.sections.flatMap((section, sectionIndex) =>
                section.fields
                  .filter((field) => renderFields.includes(field.fieldName))
                  .map((field, fieldIndex) => {
                    let newParentFieldName = "";
                    if (field.properties.ParentFieldName) {
                      newParentFieldName = field.properties.ParentFieldName + "_splitted";
                    }
                    field = {
                      ...field,
                      fieldName: field.fieldName + "_splitted",
                      properties: {
                        ...field.properties,
                        ParentFieldName: newParentFieldName,
                        Label: field.fieldName,
                      },
                    };

                    const props = {
                      field: field,
                      fieldName: field?.fieldName,
                      fieldType: field?.fieldType,
                      sectionIndex: sectionIndex,
                      fieldIndex: fieldIndex,
                      value: field?.value || "",
                      handleFieldChange: handleFieldChange,
                      labelVisible: true,
                      isTabField: false,
                      isStageField: false,
                      isPrintPreview: false,
                    };

                    return (
                      <div
                        key={field.fieldName}
                        className="k-mt-2"
                      >
                        <AdaptiveField {...props} />
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        </div>
        <Else>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Loader
              type="converging-spinner"
              themeColor="primary"
            />
          </div>
        </Else>
      </If>
      {showSplitEntryDialog.value && (
        <ConfirmSplitEntryDialog
          handleCancel={() => (showSplitEntryDialog.value = false)}
          handleConfirm={() => handleSubmit(event)}
        />
      )}
    </Window>
  );
};
